import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkIcon from '@mui/icons-material/Link';
import { JOB_PORTAL_KEYS, NS } from '../../../../../i18n/constants/keys';
import type { IShareSectionProps } from '../../interfaces';

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const ShareSection = ({
  jobPostData,
  mailtoUrl,
  copyToClipboard,
  shareOnLinkedIn,
  shareOnWhatsApp,
  shareOnFacebook,
  shareOnX,
}: IShareSectionProps) => {
  const { t } = useTranslation(NS.jobPortal);

  const handleEmailShare = (): void => {
    if (mailtoUrl) window.location.href = mailtoUrl;
  };

  return (
    <Box>
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography variant="subtitle2" fontWeight={800}>
          {t(JOB_PORTAL_KEYS.share.title)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(JOB_PORTAL_KEYS.share.subtitle)}
        </Typography>
      </Box>

      <Stack spacing={1.5}>
        {jobPostData?.id && (
          <TextField
              value={`${window.location.origin}/jobs/${jobPostData.id}`}
              InputProps={{
                readOnly: true,
                startAdornment: <LinkIcon fontSize="small" color="primary" sx={{ mr: 1 }} />,
              }}
              size="small"
              fullWidth
              inputProps={{ 'aria-label': 'Job post URL', style: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
          />
        )}

        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Button variant="outlined" fullWidth startIcon={<ContentCopyIcon fontSize="small" />} onClick={copyToClipboard}>
              {t(JOB_PORTAL_KEYS.share.copyUrl)}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button variant="contained" fullWidth startIcon={<EmailIcon fontSize="small" />} onClick={handleEmailShare}>
              {t(JOB_PORTAL_KEYS.share.emailFriend)}
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ pt: 1 }}>
          <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase' }}>
            {t(JOB_PORTAL_KEYS.share.socialNetworks)}
          </Typography>
        </Divider>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <Button variant="outlined" fullWidth startIcon={<LinkedInIcon fontSize="small" />} onClick={shareOnLinkedIn}>
              LinkedIn
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button variant="outlined" fullWidth startIcon={<WhatsAppIcon fontSize="small" />} onClick={shareOnWhatsApp}>
              WhatsApp
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button variant="outlined" fullWidth startIcon={<FacebookIcon fontSize="small" />} onClick={shareOnFacebook}>
              Facebook
            </Button>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button variant="outlined" fullWidth startIcon={<XIcon />} onClick={shareOnX}>
              X
            </Button>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
